<script lang="ts">
  import {
    BASE_RESOURCE_PER_SECOND, MAX_BUILDING_LEVEL, MAX_TOOL_TIER,
    SKILL_BY_RESOURCE, SKILL_MILESTONE_LEVELS, buildingUpgradeRecipe, canAffordResources,
    globalProductionMultiplier, globalXpMultiplier, RESOURCE_BY_SKILL, skillMultiplier,
    toolUpgradeCost, toolUpgradeRequirements, xpForLevel,
    type EconomyActionResult, type GameState, type ManualActionResult, type SkillId
  } from '@rsvjelle/game-core';
  import { formatNumber, SKILL_GROUPS, SKILL_ICONS, titleCase } from '../lib/display.ts';
  import type { AccountSummary } from '../lib/account-status.ts';

  interface Props {
    gameState: GameState;
    onSelectSkill: (skill: SkillId) => void;
    onManualAction: () => ManualActionResult;
    onBuyTool: () => EconomyActionResult;
    onBuyBuilding: () => EconomyActionResult;
    onOpenUpgrades: () => void;
    account: AccountSummary;
    onOpenAccount: () => void;
  }
  let { gameState, onSelectSkill, onManualAction, onBuyTool, onBuyBuilding, onOpenUpgrades, account, onOpenAccount }: Props = $props();
  let trainingActive = $derived(gameState.run.trainingActive !== false);
  let active = $derived(gameState.run.skills[gameState.run.activeSkill]);
  let activeResource = $derived(RESOURCE_BY_SKILL[active.id]);
  let xpFloor = $derived(xpForLevel(active.level));
  let xpCeiling = $derived(xpForLevel(active.level + 1));
  let xpProgress = $derived(active.level >= 200 ? 100 : Math.max(0, Math.min(100, ((active.xp - xpFloor) / Math.max(1, xpCeiling - xpFloor)) * 100)));
  let perSecond = $derived(BASE_RESOURCE_PER_SECOND * (1 + active.level / 10) * skillMultiplier(active) * globalProductionMultiplier(gameState));
  let xpPerSecond = $derived(5 * skillMultiplier(active) * globalXpMultiplier(gameState));
  let toolRequirements = $derived(toolUpgradeRequirements(gameState));
  let toolCost = $derived(toolUpgradeCost(gameState));
  let hallRecipe = $derived(buildingUpgradeRecipe(gameState));
  let canBuyHall = $derived(active.buildingLevel < MAX_BUILDING_LEVEL && canAffordResources(gameState, hallRecipe));
  let canBuyTool = $derived(!toolRequirements.atMaxTier && toolRequirements.meetsSkillLevel && toolRequirements.meetsHallLevel && gameState.run.gold >= toolCost);
  let nextMilestone = $derived(SKILL_MILESTONE_LEVELS.find((level) => level > active.level));
  let nextGoal = $derived.by(() => {
    if (!toolRequirements.atMaxTier) {
      if (!toolRequirements.meetsSkillLevel) return {
        title: `${active.id} level ${toolRequirements.skillLevel}`,
        detail: `${toolRequirements.skillLevel - active.level} levels until Tool tier ${toolRequirements.nextTier}`
      };
      if (!toolRequirements.meetsHallLevel) return {
        title: `${active.id} Hall ${toolRequirements.hallLevel}`,
        detail: `Upgrade the Hall to unlock Tool tier ${toolRequirements.nextTier}`
      };
      return {
        title: `Tool tier ${toolRequirements.nextTier}`,
        detail: canBuyTool ? 'Ready to buy now' : `${formatNumber(Math.max(0, toolCost - gameState.run.gold))} more gold needed`
      };
    }
    return nextMilestone
      ? { title: `${active.id} level ${nextMilestone}`, detail: `${nextMilestone - active.level} levels until the next achievement` }
      : { title: `${active.id} mastered`, detail: 'Level 200 achievement completed' };
  });
  let bursts = $state<Array<{ id: number; text: string }>>([]);
  let burstId = 0;

  function trainNow() {
    const result = onManualAction();
    const id = ++burstId;
    bursts.push({ id, text: `+${formatNumber(result.gainedResource)} ${titleCase(result.resource)}` });
    setTimeout(() => { bursts = bursts.filter((burst) => burst.id !== id); }, 900);
  }
</script>

<section class="view-stack" aria-labelledby="realm-title">
  <div class="section-heading">
    <div><p class="eyebrow">{trainingActive ? 'ACTIVE TRAINING' : 'CHOOSE YOUR PATH'}</p><h2 id="realm-title">Shape your realm</h2></div>
    <span class:waiting={!trainingActive} class="live-badge"><i></i> {trainingActive ? 'LIVE' : 'WAITING'}</span>
  </div>

  <article class:waiting={!trainingActive} class="hero-card">
    <div class="hero-glow"></div>
    {#if trainingActive}
      <div class="active-skill-icon" aria-hidden="true">{SKILL_ICONS[active.id]}</div>
      <div class="active-skill-copy">
        <p>Currently mastering</p>
        <h3>{active.id}</h3>
        <div class="level-row"><strong>Level {active.level}</strong><span>{formatNumber(active.xp - xpFloor, 0)} / {formatNumber(xpCeiling - xpFloor, 0)} XP</span></div>
        <div class="progress-track" aria-label={`${xpProgress.toFixed(0)} percent to next level`}><span style={`width:${xpProgress}%`}></span></div>
        <div class="rate-row">
          <span><b>+{formatNumber(perSecond, 2)}</b> resource/s</span>
          <span><b>+{formatNumber(xpPerSecond, 1)}</b> XP/s</span>
        </div>
      </div>
      <div class="manual-action-wrap">
        <button class="manual-action" data-onboarding="train" type="button" onclick={trainNow}>
          <span class="button-shine"></span>
          <strong>Train now</strong><small>Tap for an instant boost</small>
        </button>
        {#each bursts as burst (burst.id)}<span class="gain-burst">{burst.text}</span>{/each}
      </div>
    {:else}
      <div class="active-skill-icon" aria-hidden="true">🧭</div>
      <div class="active-skill-copy waiting-copy">
        <p>No training selected</p>
        <h3>Your next mastery is your choice</h3>
        <span>Choose any skill below to begin. Nothing is trained automatically, now or while you are offline.</span>
      </div>
    {/if}
  </article>

  {#if trainingActive}
    <div class="resource-strip">
      <div><span class="resource-rune">◆</span><span>{titleCase(activeResource)}</span></div>
      <strong>{formatNumber(gameState.run.resources[activeResource] ?? 0)}</strong>
    </div>
  {/if}

  {#if account.status === 'local' || account.status === 'attention'}
    <button class:attention={account.status === 'attention'} class="account-reminder" type="button" onclick={onOpenAccount}>
      <span>{account.status === 'attention' ? '⚠️' : '🛡️'}</span>
      <span><strong>{account.status === 'attention' ? 'Your account needs attention' : 'Protect this realm'}</strong><small>{account.status === 'attention' ? 'Open your account to restore cloud synchronization.' : 'This realm is saved on this device. Sign in to sync it across devices.'}</small></span>
      <b>{account.status === 'attention' ? 'Review →' : 'Connect →'}</b>
    </button>
  {/if}

  {#if trainingActive}
    <article class="realm-command-card">
      <div class="goal-copy">
      <p class="eyebrow">NEXT UPGRADE GOAL</p>
      <h3>{nextGoal.title}</h3>
      <small>{nextGoal.detail}</small>
      {#if nextMilestone}<span class="achievement-hint">🏆 Achievement at level {nextMilestone}</span>{/if}
      </div>
      <div class="realm-command-actions">
      <div class="quick-upgrades">
        <button type="button" disabled={!canBuyTool} onclick={onBuyTool}>
          <span>🛠️ Tool {Math.min(active.toolTier, MAX_TOOL_TIER)}/{MAX_TOOL_TIER}</span>
          <small>{toolRequirements.atMaxTier ? 'Maximum tier' : canBuyTool ? `Buy tier ${toolRequirements.nextTier}` : `Lv ${toolRequirements.skillLevel} · Hall ${toolRequirements.hallLevel} · ${formatNumber(toolCost)} gold`}</small>
        </button>
        <button type="button" disabled={!canBuyHall} onclick={onBuyBuilding}>
          <span>🏛️ Hall {Math.min(active.buildingLevel, MAX_BUILDING_LEVEL)}/{MAX_BUILDING_LEVEL}</span>
          <small>{active.buildingLevel >= MAX_BUILDING_LEVEL ? 'Maximum Hall' : canBuyHall ? `Build level ${active.buildingLevel + 1}` : 'Gather both support resources'}</small>
        </button>
        <button class="open-workshop" type="button" onclick={onOpenUpgrades}>Workshop →</button>
      </div>
      {#if active.buildingLevel < MAX_BUILDING_LEVEL}
        <div class="inline-supply-routes">
          <span><strong>Hall supplies</strong><small>Tap a skill to train it</small></span>
          <div class="support-route-grid">
            {#each Object.entries(hallRecipe) as [supportResource, amount]}
              {@const supportSkill = SKILL_BY_RESOURCE[supportResource]}
              <button type="button" onclick={() => onSelectSkill(supportSkill)}>
                <span class="skill-icon">{SKILL_ICONS[supportSkill]}</span>
                <span><strong>{supportSkill}</strong><small>{titleCase(supportResource)}</small></span>
                <b class:ready={(gameState.run.resources[supportResource] ?? 0) >= amount}>{formatNumber(gameState.run.resources[supportResource] ?? 0)} / {formatNumber(amount)}</b>
              </button>
            {/each}
          </div>
        </div>
      {/if}
      </div>
    </article>
  {/if}

  <div class="skills-panel">
    <div class="panel-title"><div><p class="eyebrow">SKILL CODEX</p><h3>Choose your path</h3></div><span>{Object.keys(gameState.run.skills).length} skills</span></div>
    {#each SKILL_GROUPS as group}
      <div class="skill-group">
        <h4>{group.label}</h4>
        <div class="skill-grid">
          {#each group.skills as skillId}
            {@const skill = gameState.run.skills[skillId]}
            <button class:active={trainingActive && gameState.run.activeSkill === skillId} data-onboarding-skill={skillId} type="button" onclick={() => onSelectSkill(skillId)}>
              <span class="skill-icon">{SKILL_ICONS[skillId]}</span>
              <span class="skill-name">{skillId}</span>
              <span class="skill-level">Lv {skill.level}</span>
            </button>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</section>
